import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * Promotions: coupon campaigns for Premium.
 *
 * The discount itself lives in the stores. A campaign ties the code a user
 * types (`STEADO50`) to an Apple Offer Code (attributed by its reference name,
 * which RevenueCat reports as `offer_code`) and a Google Play offer with
 * developer-determined eligibility. The console registers campaigns and reads
 * their numbers: claims typed in the app, and the purchases RevenueCat
 * reports. It cannot change a price: that is App Store Connect and Play
 * Console.
 */
export const PROMO_PLANS = ['annual', 'monthly', 'both'];
export const PROMO_REDEMPTION_SOURCES = ['app_claim', 'store_direct'];
export const PROMO_REDEMPTION_STATUSES = ['claimed', 'purchased'];
/** Derived from the dates and `archivedAt`, so the list can say it without arithmetic. */
export const PROMO_CAMPAIGN_STATES = ['scheduled', 'live', 'ended', 'archived'];
/** Upper-case letters and digits; what users type, compared case-insensitively. */
export const PROMO_CODE_PATTERN = /^[A-Z0-9]{4,32}$/;
const storeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9._-]+$/, 'Letters, digits, dots, dashes and underscores.');
export const adminPromoStatsSchema = z.object({
    /** Redemptions of any kind: claims typed in the app plus store-direct purchases. */
    redemptions: z.number().int(),
    claims: z.number().int(),
    purchases: z.number().int(),
    purchasesIos: z.number().int(),
    purchasesAndroid: z.number().int(),
    /** Purchases RevenueCat attributed without a claim: the code was redeemed in the App Store. */
    storeDirect: z.number().int(),
    /** Purchasers who still have Premium through a store subscription. */
    activeSubscribers: z.number().int(),
    /** First-purchase revenue per currency, as the store reported it. */
    revenue: z.array(z.object({ currency: z.string(), amount: z.number() })),
});
export const adminPromoCampaignSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    discountLabel: z.string(),
    appliesTo: z.enum(PROMO_PLANS),
    appleOfferReference: z.string().nullable(),
    appleCustomCode: z.string().nullable(),
    playBasePlanId: z.string().nullable(),
    playOfferId: z.string().nullable(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    maxClaims: z.number().int().nullable(),
    newSubscribersOnly: z.boolean(),
    archivedAt: z.string().nullable(),
    state: z.enum(PROMO_CAMPAIGN_STATES),
    stats: adminPromoStatsSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const adminPromoListQuerySchema = z.object({
    /**
     * Archived campaigns are hidden unless asked for. Kept as the query string's
     * own text: the route validates it and the controller parses it again, so a
     * transform here would meet its own output the second time.
     */
    includeArchived: z.enum(['true', 'false']).optional(),
});
export const adminPromoParamsSchema = z.object({ id: z.string().uuid() });
const campaignFields = {
    name: z.string().trim().min(1).max(120),
    discountLabel: z.string().trim().min(1).max(120),
    appliesTo: z.enum(PROMO_PLANS),
    appleOfferReference: storeIdSchema.nullable(),
    appleCustomCode: z
        .string()
        .trim()
        .max(64)
        .regex(/^[A-Za-z0-9]+$/, 'Apple custom codes are letters and digits.')
        .nullable(),
    playBasePlanId: storeIdSchema.nullable(),
    playOfferId: storeIdSchema.nullable(),
    startsAt: z.string().datetime({ offset: true }).nullable(),
    endsAt: z.string().datetime({ offset: true }).nullable(),
    maxClaims: z.number().int().min(1).max(1_000_000).nullable(),
    newSubscribersOnly: z.boolean(),
};
export const adminPromoCreateSchema = z.object({
    code: z
        .string()
        .trim()
        .transform((code) => code.toUpperCase())
        .pipe(z.string().regex(PROMO_CODE_PATTERN, 'A code is 4 to 32 letters and digits.')),
    ...campaignFields,
});
/** Every field optional. The code and the store ids are refused once anyone has redeemed. */
export const adminPromoUpdateSchema = z.object(campaignFields).partial();
export const adminPromoRedemptionSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    displayName: z.string().nullable(),
    phone: z.string(),
    source: z.enum(PROMO_REDEMPTION_SOURCES),
    status: z.enum(PROMO_REDEMPTION_STATUSES),
    platform: z.string().nullable(),
    claimedAt: z.string().nullable(),
    purchasedAt: z.string().nullable(),
    productId: z.string().nullable(),
    price: z.number().nullable(),
    currency: z.string().nullable(),
    /** Whether the user has Premium right now. */
    premiumNow: z.boolean(),
    createdAt: z.string(),
});
export const adminPromoRedemptionListQuerySchema = cursorPageQuerySchema.extend({
    status: z.enum(PROMO_REDEMPTION_STATUSES).optional(),
});
//# sourceMappingURL=promotions.js.map